/** Dashboard stat card — single number with a label + optional
 * delta hint ("+12 this month").
 *
 * Accent color picks the icon background; defaults to the cvp-accent
 * sky. Pass `tone="warn"` etc. for amber / emerald / rose tints.
 */
import { type ReactNode } from "react";


type Tone = "info" | "ok" | "warn" | "rose";

const toneStyles: Record<Tone, string> = {
  info: "bg-sky-500/15 text-sky-300",
  ok: "bg-emerald-500/15 text-emerald-300",
  warn: "bg-amber-500/15 text-amber-300",
  rose: "bg-rose-500/15 text-rose-300",
};


interface Props {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
}


export function StatCard({ label, value, delta, icon, tone = "info" }: Props) {
  return (
    <div className="card group hover:border-nb-border transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-nb-text-3">
            {label}
          </div>
          <div className="mt-2 text-3xl font-semibold text-nb-text leading-none">
            {value}
          </div>
          {delta && (
            <div className="mt-2 text-xs text-nb-text-2">{delta}</div>
          )}
        </div>
        {icon && (
          <div
            className={
              "shrink-0 grid h-10 w-10 place-items-center rounded-lg " +
              toneStyles[tone]
            }
          >
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}
